import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/ui/use-toast.ts";
import { FAQ } from "@/types/FAQ.ts";
import { createFAQ, deleteFAQ, getFAQs, updateFAQ } from "@/services/faqService.ts";
import { TableFloatUI } from "@/components/ui/table-floatui.tsx";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";

const FAQPage = () => {
    const { t } = useTranslation();
    const [FAQs, setFAQs] = useState<FAQ[]>([]);
    const [itemToDelete, setItemToDelete] = useState<FAQ | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const columnMap = {
        question: "Question",
        questionFr: "Question (French)",
        answer: "Answer",
        answerFr: "Answer (French)",
    };

    useEffect(() => {
        const fetchFAQs = async () => {
            try {
                const data = await getFAQs();
                setFAQs(data);
            } catch (error) {
                console.error("Error fetching FAQ:", error);
            }
        };

        fetchFAQs();
    }, []);

    const handleSave = async (faq: FAQ, id: number | null = null) => {
        try {
            if (id) {
                await updateFAQ(id, faq);
                toast({
                    title: t("common.successTitle"),
                    description: t("settings.faq.updateSuccess"),
                });
            } else {
                await createFAQ(faq);
                toast({
                    title: t("common.successTitle"),
                    description: t("settings.faq.createSuccess"),
                });
            }
            const data = await getFAQs();
            setFAQs(data);
        } catch (error) {
            console.error("Error:", id ? "updating FAQ" : "creating FAQ", error);
        }
    };

    const handleDeleteRequest = useCallback((item: FAQ) => {
        setItemToDelete(item);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await deleteFAQ(itemToDelete.id!);
            setFAQs((prev) => prev.filter((faq) => faq.id !== itemToDelete.id));
            toast({
                title: t("common.successTitle"),
                description: t("settings.faq.deleteSuccess"),
            });
            setItemToDelete(null);
        } catch (error) {
            console.error("Error deleting FAQ:", error);
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: t("common.errorDesc"),
            });
        } finally {
            setIsDeleting(false);
        }
    }, [itemToDelete, t]);

    const handleCancelDelete = useCallback(() => {
        setItemToDelete(null);
    }, []);

    return (
        <div className="space-y-6">
            <TableFloatUI
                tableItems={FAQs}
                tableTitle={t("common.faq")}
                columns={columnMap}
                saveHandler={handleSave}
                deleteHandler={handleDeleteRequest}
            />

            <AlertDialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("common.areYouSure")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {itemToDelete && (
                                <>
                                    {t("settings.faq.deleteConfirmDesc")}
                                    {(itemToDelete.question || itemToDelete.questionFr) && (
                                        <span className="mt-2 block font-medium text-foreground">
                                            {t("common.delete")}: {itemToDelete.question ?? itemToDelete.questionFr ?? `#${itemToDelete.id}`}
                                        </span>
                                    )}
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelDelete} disabled={isDeleting}>
                            {t("common.cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleConfirmDelete();
                            }}
                            disabled={isDeleting}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isDeleting ? t("common.loading") : t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default FAQPage;
